import React, { FC, useState, useEffect } from "react";
// material-ui
import {
  Box,
  Grid,
  Button,
  Modal,
} from "@mui/material";
import axios from "../../utils/axios";
import OutlinedCard from "../Cards/Card";
import { Utils } from "../../utils/common-utils";

export interface IDialogProps {
  title: string;
  open: boolean;
  onClose: () => void;
  // code of the account's current plan
  currentPlan?: string;
  // opens the request form (upgrade / Contact sales)
  onRequest?: (type: 'upgrade' | 'enterprise', planCode?: string) => void;
}

interface IPlan {
  code: string;
  name: string;
  pricing?: { monthly?: number; yearly?: number } | null;
  limits?: Record<string, number>;
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 1120,
  maxWidth: '96vw',
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  padding: 0
};

const PLAN_HIGHLIGHT: Record<string, string> = {
  starter: 'GST invoices, purchases, payments and basic reports',
  business: 'Purchase orders, GRN, accounting, HR basics and audit log',
  pro: 'Payroll, CRM, approvals and every module',
  enterprise: 'API, integrations, SLA and custom infrastructure',
};

// -1 = unlimited
const limitText = (limit: number | undefined, one: string, many: string) => {
  if (limit === undefined || limit === null) return null;
  if (limit < 0) return `Unlimited ${many}`;
  return `${limit.toLocaleString('en-IN')} ${limit === 1 ? one : many}`;
};

const storageText = (mb: number | undefined) => {
  if (mb === undefined || mb === null) return null;
  if (mb < 0) return 'Pooled file storage';
  return `${mb >= 1024 ? `${mb / 1024} GB` : `${mb} MB`} file storage`;
};

const planFeatures = ({ code, limits = {} }: IPlan) => [
  PLAN_HIGHLIGHT[code],
  limitText(limits.users, 'user', 'users'),
  limitText(limits.locations, 'location', 'locations'),
  limitText(limits.invoicesPerMonth, 'invoice per month', 'invoices per month'),
  limitText(limits.items, 'item', 'items'),
  storageText(limits.storageMB),
].filter(Boolean);

const planPrice = ({ pricing }: IPlan) => {
  if (!pricing) return 'Call us';
  return pricing.yearly ? pricing.yearly : 'Free';
};

const planPriceDesc = ({ pricing }: IPlan) => {
  if (!pricing) return 'for price';
  if (!pricing.yearly) return 'forever';
  return `per year + GST\nor ${Utils.formatNumber(pricing.monthly)} per month`;
};

// Plan catalog dialog. Plans are activated by the Wareozo team, so this is view only.
const SettingDialog: FC<IDialogProps> = ({
  title,
  onClose,
  open,
  currentPlan,
  onRequest,
}) => {
  const [plans, setPlans] = useState<IPlan[]>([]);
  // plan the customer clicked to request
  const [selected, setSelected] = useState('');

  useEffect(() => {
    let isMounted = true;
    const fetchPlans = async () => {
      try {
        const response = await axios.get<{ plans: IPlan[] }>("/api/v1/subscription/plans");
        if (isMounted) {
          setPlans(response.data.plans || []);
        }
      } catch (error: any) {
        // Empty catch block.
      }
    };
    fetchPlans();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        {/* Header with title and action buttons - matching Payment Update style */}
        <Grid container>
          <Grid
            xs={12}
            sm={12}
            md={12}
            lg={12}
            p={2}
            pl={2}
            bgcolor="#e9ecf1"
            style={{
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              height: 50
            }}
          >
            <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
              <Button
                style={{
                  color: '#488dff',
                  backgroundColor: '#fff',
                  border: '1px solid #488dff',
                  marginRight: 10
                }}
                variant="contained"
                size="small"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
            <h3 style={{ marginTop: -2 }}>{title}</h3>
          </Grid>
        </Grid>

        {/* Content Area */}
        <Grid container p={2} spacing={2}>
          <Grid item xs={12} md={12} sm={12}>
            <Box
              sx={{
                mt: 2,
                backgroundColor: "#fafafb",
                borderRadius: "8px",
                border: '1.5px solid #e9ecf1'
              }}
            >
              <Grid container spacing={2} sx={{ width: "100%", pl: 2, py: "0px" }}>
                {plans.map((plan) => (
                  <Grid item xs={3} key={plan.code} onClick={() => onRequest && plan.code !== currentPlan && plan.code !== 'starter' && setSelected(plan.code)}>
                    <OutlinedCard
                      value={plan.name.toUpperCase()}
                      tag={plan.code === currentPlan ? '(Current plan)' : plan.code === selected ? '(Selected)' : undefined}
                      featuresData={planFeatures(plan)}
                      featurePrice={planPrice(plan)}
                      featurePriceDesc={planPriceDesc(plan)}
                      border={plan.code === selected ? '2px solid #059669' : plan.code === currentPlan ? "2px solid  rgb(24, 144, 255) " : "2px solid transparent"}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 1.5 }}>
              <h5 style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 13, margin: 0, flex: 1, minWidth: 0 }}>
                {onRequest ? 'Click a plan to select it, then send a request. ' : ''}Plans are activated by the Wareozo team. Extra users and locations can be added to Business and Pro.
              </h5>
              {onRequest && (
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <Button size='small' variant='outlined' onClick={() => onRequest('enterprise')} sx={{ textTransform: 'none' }}>Contact sales (Enterprise)</Button>
                  <Button size='small' variant='contained' onClick={() => onRequest(selected === 'enterprise' ? 'enterprise' : 'upgrade', selected || undefined)} sx={{ textTransform: 'none' }}>
                    {selected ? `Request ${plans.find((p) => p.code === selected)?.name || 'upgrade'}` : 'Request upgrade'}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default SettingDialog;
